import { useEffect, useState, useCallback } from "react";
import Iframe from "../../../components/Iframe";
import Breadcrumbs from "../../../components/Breadcrumbs";
import {
  handleInputChangeWithScroll,
  MESSAGE_TYPES,
} from "../../../utils/editUtils";
import MediaSelector from "../../../components/website/MediaSelector";
import MediaGallery from "../../../components/website/MediaGallery";
import { MediaProvider, useMedia } from "../../../contexts/MediaContext";
import ResizableEditArea from "../../../components/common/ResizableEditArea";
import {
  executeValidationWithArrays,
  handleInputChangeWithValidation,
  handleResetWithValidation,
} from "../../../utils/formValidation";

// パンくず
const breadcrumbs = [
  { title: "Home", path: `/` },
  { title: "New Visitor", path: `/newvisitor` },
];

function NewVisitorEditPageContent() {
  const { isMediaOpen, fetchMediaFiles, selectMedia } = useMedia();

  const contentKey = "newVisitorContent";
  const [message, setMessage] = useState("");
  const [loadingText, setLoadingText] = useState("");

  const [pageContent, setPageContent] = useState({});
  const [pcVideoSmall, setPcVideoSmall] = useState(null);
  const [pcVideoMedium, setPcVideoMedium] = useState(null);
  const [pcVideoLarge, setPcVideoLarge] = useState(null);
  const [pcVideoExtraLarge, setPcVideoExtraLarge] = useState(null);
  const [spVideoSmall, setSpVideoSmall] = useState(null);
  const [spVideoMedium, setSpVideoMedium] = useState(null);
  const [spVideoLarge, setSpVideoLarge] = useState(null);
  const [spVideoExtraLarge, setSpVideoExtraLarge] = useState(null);
  const [empathyBgImage, setEmpathyBgImage] = useState(null);
  const [appealBgImage, setAppealBgImage] = useState(null);
  const [conversionContents, setConversionContents] = useState([
    { text: "", button_text: "", image: null },
    { text: "", button_text: "", image: null },
    { text: "", button_text: "", image: null },
    { text: "", button_text: "", image: null },
    { text: "", button_text: "", image: null },
  ]);
  const [reasonsContents, setReasonsContents] = useState([]);

  const [hasChanges, setHasChanges] = useState(false);

  // messageが文字列の場合、3秒後に自動で消去
  useEffect(() => {
    if (message && message.trim() !== "") {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // ファイルアップロードハンドラー
  const handleDrop = useCallback(
    async (e) => {
      if (e.preventDefault) {
        e.preventDefault();
      }

      setLoadingText("アップロード中");

      const file = e.target?.files?.[0] || e.dataTransfer?.files?.[0];

      if (!file) {
        setLoadingText("");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("images", file);
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(`${apiUrl}/api/image_upload/`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message);

        if (data.images?.[0]) {
          const uploadedImage = {
            id: data.images[0].id,
            image_url: data.images[0].url || data.images[0].image_url,
            mime_type: data.images[0].mime_type,
            width: data.images[0].width,
            height: data.images[0].height,
            file_size: data.images[0].file_size,
          };

          await fetchMediaFiles();
          selectMedia(uploadedImage, true);
        }
      } catch (error) {
        console.error("アップロードエラー:", error);
      }
      setLoadingText("");
    },
    [fetchMediaFiles, selectMedia, setLoadingText]
  );

  // データ取得
  const fetchPageContentData = useCallback(async () => {
    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_page_new_visitor_content/`, { credentials: "include" });
      if (!response.ok) {
        throw new Error("ネットワークレスポンスが異常です。");
      }
      const data = await response.json();
      setPageContent(data);
      setPcVideoSmall(data.pc_video_small);
      setPcVideoMedium(data.pc_video_medium);
      setPcVideoLarge(data.pc_video_large);
      setPcVideoExtraLarge(data.pc_video_extra_large);
      setSpVideoSmall(data.mobile_video_small);
      setSpVideoMedium(data.mobile_video_medium);
      setSpVideoLarge(data.mobile_video_large);
      setSpVideoExtraLarge(data.mobile_video_extra_large);
      setEmpathyBgImage(data.empathy_background_image);
      setAppealBgImage(data.appeal_background_image);

      // コンバージョンコンテンツ（5箇所固定）
      const convData = data.conversion_contents || [];
      const filledConv = Array.from({ length: 5 }, (_, i) => ({
        text: convData[i]?.text || "",
        button_text: convData[i]?.button_text || "",
        image: convData[i]?.image || null,
      }));
      setConversionContents(filledConv);

      // 選ばれる理由コンテンツ
      setReasonsContents(data.reasons_contents || []);

      setHasChanges(false);
    } catch (error) {
      console.error("データの取得中にエラーが発生しました:", error);
    }
    setLoadingText("");
  }, []);

  useEffect(() => {
    fetchPageContentData();
    fetchMediaFiles();
  }, [fetchPageContentData, fetchMediaFiles]);

  // データ更新
  const handlePageDataSubmit = async () => {
    const formData = {
      page_content: pageContent,
      pc_video_small: pcVideoSmall ? pcVideoSmall.id : null,
      pc_video_medium: pcVideoMedium ? pcVideoMedium.id : null,
      pc_video_large: pcVideoLarge ? pcVideoLarge.id : null,
      pc_video_extra_large: pcVideoExtraLarge ? pcVideoExtraLarge.id : null,
      mobile_video_small: spVideoSmall ? spVideoSmall.id : null,
      mobile_video_medium: spVideoMedium ? spVideoMedium.id : null,
      mobile_video_large: spVideoLarge ? spVideoLarge.id : null,
      mobile_video_extra_large: spVideoExtraLarge ? spVideoExtraLarge.id : null,
      empathy_background_image: empathyBgImage ? empathyBgImage.id : null,
      appeal_background_image: appealBgImage ? appealBgImage.id : null,
      conversion_contents: conversionContents.map((conv) => ({
        text: conv.text,
        button_text: conv.button_text,
        image_id: conv.image ? conv.image.id : null,
        image: conv.image,
      })),
      reasons_contents: reasonsContents,
    };

    setLoadingText("保存中");

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/update_page_new_visitor_content/`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(`${data.message} status: ${response.status}`);
      }
      setMessage(data.message);
      setHasChanges(false);
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
    }
    setLoadingText("");
  };

  // テキスト入力ハンドラー
  const handleInputChange = (e) => {
    handleInputChangeWithValidation(
      e,
      setPageContent,
      handleInputChangeWithScroll,
      {
        contentKey,
        currentData: pageContent,
        setData: setPageContent,
      }
    );
    setHasChanges(true);
  };

  // メディア選択ハンドラー
  const handleMediaSelect = (setMediaFunction, fieldName) => (media) => {
    const element = document.querySelector(`[data-field="${fieldName}"]`);
    if (element) {
      const errorClass = "validation-error";
      const labelClass = "validation-error-label";
      const wrapperClass = "form-field-wrapper";

      if (element.classList.contains(errorClass)) {
        element.classList.remove(errorClass);
        const mediaSelector = element.closest(".media-selector");
        if (mediaSelector) {
          mediaSelector.classList.remove(errorClass);
        }
        const wrapper = element.closest(`.${wrapperClass}`);
        if (wrapper) {
          const errorLabel = wrapper.querySelector(`.${labelClass}`);
          if (errorLabel) {
            errorLabel.remove();
          }
        }
      }
    }

    setMediaFunction(media);
    setHasChanges(true);
  };

  // コンバージョンコンテンツの入力ハンドラー
  const handleConversionInputChange = (index, field, value) => {
    setConversionContents((prev) => {
      const newConversions = [...prev];
      newConversions[index] = { ...newConversions[index], [field]: value };

      // iframeに通知
      sendToIframe({
        type: MESSAGE_TYPES.TEXT_UPDATE,
        contentKey,
        allData: {
          ...pageContent,
          conversion_contents: newConversions.map((c) => ({
            ...c,
            image_url: c.image?.image_url || "",
          })),
        },
      });

      return newConversions;
    });
    setHasChanges(true);
  };

  // コンバージョンの画像選択ハンドラー
  const handleConversionImageSelect = (index) => (media) => {
    setConversionContents((prev) => {
      const newConversions = [...prev];
      newConversions[index] = { ...newConversions[index], image: media };

      // iframeに通知
      sendToIframe({
        type: MESSAGE_TYPES.TEXT_UPDATE,
        contentKey,
        allData: {
          ...pageContent,
          conversion_contents: newConversions.map((c) => ({
            ...c,
            image_url: c.image?.image_url || "",
          })),
        },
      });

      return newConversions;
    });
    setHasChanges(true);
  };

  // 選ばれる理由の入力ハンドラー
  const handleReasonInputChange = (index, field, value) => {
    setReasonsContents((prev) => {
      const newReasons = [...prev];
      newReasons[index] = { ...newReasons[index], [field]: value };

      sendToIframe({
        type: MESSAGE_TYPES.TEXT_UPDATE,
        contentKey,
        allData: {
          ...pageContent,
          reasons_contents: newReasons,
        },
      });

      return newReasons;
    });
    setHasChanges(true);
  };

  // 選ばれる理由の画像更新ハンドラー
  const handleReasonImageSelect = (index) => (media) => {
    setReasonsContents((prev) => {
      const newReasons = [...prev];
      newReasons[index] = {
        ...newReasons[index],
        image: media,
        image_url: media ? media.image_url : "",
      };

      sendToIframe({
        type: MESSAGE_TYPES.TEXT_UPDATE,
        contentKey,
        allData: {
          ...pageContent,
          reasons_contents: newReasons,
        },
      });

      return newReasons;
    });
    setHasChanges(true);
  };

  // 選ばれる理由の追加
  const handleAddReason = (index) => {
    const newReasons = [...reasonsContents];
    newReasons.splice(index + 1, 0, { title: "", text: "", image_url: "" });
    setReasonsContents(newReasons);
    setHasChanges(true);

    sendToIframe({
      type: MESSAGE_TYPES.TEXT_UPDATE,
      contentKey,
      allData: {
        ...pageContent,
        reasons_contents: newReasons,
      },
    });
  };

  // 選ばれる理由の削除
  const handleDeleteReason = (index) => {
    const newReasons = [...reasonsContents];
    newReasons.splice(index, 1);
    if (newReasons.length === 0)
      newReasons.push({ title: "", text: "", image_url: "" });
    setReasonsContents(newReasons);
    setHasChanges(true);

    sendToIframe({
      type: MESSAGE_TYPES.TEXT_UPDATE,
      contentKey,
      allData: {
        ...pageContent,
        reasons_contents: newReasons,
      },
    });
  };

  // iframe通信ヘルパー
  const sendToIframe = (messageData) => {
    const iframe = document.querySelector("iframe");
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        messageData,
        process.env.REACT_APP_ORIGIN
      );
    }
  };

  // iframeスクロール命令を送信するヘルパー
  const scrollIframeTo = (targetSelector) => {
    sendToIframe({
      type: MESSAGE_TYPES.SCROLL_TO,
      target: targetSelector,
      contentKey,
    });
  };

  // リセット処理
  const handleReset = () => {
    handleResetWithValidation(fetchPageContentData);
  };

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            {/* パンくず */}
            <Breadcrumbs
              breadcrumbs={breadcrumbs}
              onResizeStart={onResizeStart}
            />

            <div className={`message ${message ? "success" : ""}`}>
              <p>{message}</p>
            </div>

            {/* 編集フォーム */}
            <div className="form">
              {/* ヒーローヘッダー */}
              <section>
                <h4>ヒーローヘッダー</h4>
                <div className="area1">
                  <textarea
                    className="input2"
                    name="hero_hedder_title"
                    value={pageContent?.hero_hedder_title || ""}
                    onChange={handleInputChange}
                    onFocus={handleInputChange}
                    data-label="ヒーローヘッダー タイトル"
                    rows={3}
                    placeholder="タイトルを入力"
                  />
                  <textarea
                    className="input2"
                    name="hero_hedder_description"
                    value={pageContent?.hero_hedder_description || ""}
                    onChange={handleInputChange}
                    onFocus={handleInputChange}
                    data-label="ヒーローヘッダー 説明"
                    rows={5}
                    placeholder="説明を入力"
                  />
                </div>

                <div className="img-form">
                  {/* PC用動画 */}
                  <div className="h5-contents">
                    <h5>PC用動画(小)</h5>
                    <MediaSelector
                      label="PC用動画(小)"
                      maxSize="50MB"
                      type="video"
                      fieldName="pc_video_small"
                      selectedMedia={pcVideoSmall}
                      onSelect={handleMediaSelect(
                        setPcVideoSmall,
                        "pc_video_small"
                      )}
                    />
                  </div>
                  <div className="h5-contents">
                    <h5>PC用動画(中)</h5>
                    <MediaSelector
                      label="PC用動画(中)"
                      maxSize="50MB"
                      type="video"
                      fieldName="pc_video_medium"
                      selectedMedia={pcVideoMedium}
                      onSelect={handleMediaSelect(
                        setPcVideoMedium,
                        "pc_video_medium"
                      )}
                    />
                  </div>
                  <div className="h5-contents">
                    <h5>PC用動画(大)</h5>
                    <MediaSelector
                      label="PC用動画(大)"
                      maxSize="50MB"
                      type="video"
                      fieldName="pc_video_large"
                      selectedMedia={pcVideoLarge}
                      onSelect={handleMediaSelect(
                        setPcVideoLarge,
                        "pc_video_large"
                      )}
                    />
                  </div>
                  <div className="h5-contents">
                    <h5>PC用動画(特大)</h5>
                    <MediaSelector
                      label="PC用動画(特大)"
                      maxSize="50MB"
                      type="video"
                      fieldName="pc_video_extra_large"
                      selectedMedia={pcVideoExtraLarge}
                      onSelect={handleMediaSelect(
                        setPcVideoExtraLarge,
                        "pc_video_extra_large"
                      )}
                    />
                  </div>

                  {/* スマホ用動画 */}
                  <div className="h5-contents">
                    <h5>スマホ用動画(小)</h5>
                    <MediaSelector
                      label="スマホ用動画(小)"
                      maxSize="50MB"
                      type="video"
                      fieldName="mobile_video_small"
                      selectedMedia={spVideoSmall}
                      onSelect={handleMediaSelect(
                        setSpVideoSmall,
                        "mobile_video_small"
                      )}
                    />
                  </div>
                  <div className="h5-contents">
                    <h5>スマホ用動画(中)</h5>
                    <MediaSelector
                      label="スマホ用動画(中)"
                      maxSize="50MB"
                      type="video"
                      fieldName="mobile_video_medium"
                      selectedMedia={spVideoMedium}
                      onSelect={handleMediaSelect(
                        setSpVideoMedium,
                        "mobile_video_medium"
                      )}
                    />
                  </div>
                  <div className="h5-contents">
                    <h5>スマホ用動画(大)</h5>
                    <MediaSelector
                      label="スマホ用動画(大)"
                      maxSize="50MB"
                      type="video"
                      fieldName="mobile_video_large"
                      selectedMedia={spVideoLarge}
                      onSelect={handleMediaSelect(
                        setSpVideoLarge,
                        "mobile_video_large"
                      )}
                    />
                  </div>
                  <div className="h5-contents">
                    <h5>スマホ用動画(特大)</h5>
                    <MediaSelector
                      label="スマホ用動画(特大)"
                      maxSize="50MB"
                      type="video"
                      fieldName="mobile_video_extra_large"
                      selectedMedia={spVideoExtraLarge}
                      onSelect={handleMediaSelect(
                        setSpVideoExtraLarge,
                        "mobile_video_extra_large"
                      )}
                    />
                  </div>
                </div>
              </section>

              {/* コンバージョン 1〜5 */}
              <section>
                <h4>予約ボタン（5箇所）</h4>
                <div className="area2">
                  {conversionContents.map((conv, index) => (
                    <div className="item" key={index}>
                      <div className="ctrl">
                        <p>{index + 1}</p>
                      </div>
                      <div className="forms">
                        <input
                          type="text"
                          className="input1"
                          value={conv.text || ""}
                          placeholder="テキストを入力"
                          onChange={(e) =>
                            handleConversionInputChange(
                              index,
                              "text",
                              e.target.value
                            )
                          }
                          onFocus={() =>
                            scrollIframeTo(`.edit_conversion_${index}`)
                          }
                          data-label={`コンバージョン${index + 1} テキスト`}
                        />
                        <input
                          type="text"
                          className="input2"
                          value={conv.button_text || ""}
                          placeholder="ボタンテキストを入力"
                          onChange={(e) =>
                            handleConversionInputChange(
                              index,
                              "button_text",
                              e.target.value
                            )
                          }
                          onFocus={() =>
                            scrollIframeTo(`.edit_conversion_${index}`)
                          }
                          data-label={`コンバージョン${index + 1} ボタンテキスト`}
                        />
                        <div className="input3 img-form">
                          <MediaSelector
                            label={`コンバージョン画像 ${index + 1}`}
                            type="image"
                            fieldName={`conversion_contents_${index}_image`}
                            selectedMedia={conv.image}
                            onSelect={handleConversionImageSelect(index)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* 共感・問題提起 */}
              <section>
                <h4>共感・問題提起セクション</h4>
                <div className="area1">
                  <textarea
                    className="input2"
                    name="empathy_text"
                    value={pageContent?.empathy_text || ""}
                    onChange={handleInputChange}
                    onFocus={handleInputChange}
                    data-label="共感・問題提起 テキスト"
                    rows={5}
                    placeholder="テキストを入力"
                  />
                </div>
                <div className="img-form">
                  <div className="h5-contents">
                    <h5>背景画像</h5>
                    <MediaSelector
                      label="共感・問題提起 背景画像"
                      type="image"
                      fieldName="empathy_background_image"
                      selectedMedia={empathyBgImage}
                      onSelect={handleMediaSelect(
                        setEmpathyBgImage,
                        "empathy_background_image"
                      )}
                    />
                  </div>
                </div>
              </section>

              {/* 選ばれる理由 */}
              <section>
                <h4>選ばれる理由</h4>
                <div className="area1">
                  <input
                    type="text"
                    className="input1"
                    name="reasons_main_title"
                    value={pageContent?.reasons_main_title || ""}
                    onChange={handleInputChange}
                    onFocus={handleInputChange}
                    data-label="選ばれる理由 メインタイトル"
                    placeholder="メインタイトルを入力"
                  />
                </div>
                <div className="area2">
                  {reasonsContents.map((reason, index) => (
                    <div className="item" key={index}>
                      <div className="ctrl">
                        <p>{index + 1}</p>
                        <div className="icon-wrap">
                          <i
                            className="icon delete"
                            onClick={() => handleDeleteReason(index)}
                          ></i>
                          <i
                            className="icon add"
                            onClick={() => handleAddReason(index)}
                          ></i>
                        </div>
                      </div>
                      <div className="forms">
                        <input
                          type="text"
                          className="input1"
                          value={reason.title || ""}
                          placeholder="タイトルを入力"
                          onChange={(e) =>
                            handleReasonInputChange(
                              index,
                              "title",
                              e.target.value
                            )
                          }
                          onFocus={() =>
                            scrollIframeTo(`.edit_reason_${index}`)
                          }
                          data-label={`選ばれる理由${index + 1} タイトル`}
                        />
                        <textarea
                          className="input2"
                          value={reason.text || ""}
                          placeholder="テキストを入力"
                          onChange={(e) =>
                            handleReasonInputChange(
                              index,
                              "text",
                              e.target.value
                            )
                          }
                          onFocus={() =>
                            scrollIframeTo(`.edit_reason_${index}`)
                          }
                          data-label={`選ばれる理由${index + 1} テキスト`}
                          rows={3}
                        />
                        <div className="input3 img-form">
                          <MediaSelector
                            label={`選ばれる理由 画像 ${index + 1}`}
                            type="image"
                            fieldName={`reasons_contents_${index}_image`}
                            selectedMedia={reason.image || null}
                            onSelect={handleReasonImageSelect(index)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {reasonsContents.length === 0 && (
                    <div className="item">
                      <div className="ctrl">
                        <div className="icon-wrap">
                          <i
                            className="icon add"
                            onClick={() => {
                              setReasonsContents([
                                { title: "", text: "", image_url: "" },
                              ]);
                              setHasChanges(true);
                            }}
                          ></i>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </section>

              {/* アピール */}
              <section>
                <h4>アピールセクション</h4>
                <div className="area1">
                  <input
                    type="text"
                    className="input1"
                    name="appeal_title"
                    value={pageContent?.appeal_title || ""}
                    onChange={handleInputChange}
                    onFocus={handleInputChange}
                    data-label="アピール タイトル"
                    placeholder="タイトルを入力"
                  />
                  <textarea
                    className="input2"
                    name="appeal_text"
                    value={pageContent?.appeal_text || ""}
                    onChange={handleInputChange}
                    onFocus={handleInputChange}
                    data-label="アピール テキスト"
                    rows={5}
                    placeholder="テキストを入力"
                  />
                </div>
                <div className="img-form">
                  <div className="h5-contents">
                    <h5>背景画像</h5>
                    <MediaSelector
                      label="アピール 背景画像"
                      type="image"
                      fieldName="appeal_background_image"
                      selectedMedia={appealBgImage}
                      onSelect={handleMediaSelect(
                        setAppealBgImage,
                        "appeal_background_image"
                      )}
                    />
                  </div>
                </div>
              </section>

              {/* クロージング */}
              <section>
                <h4>クロージングセクション</h4>
                <div className="area1">
                  <textarea
                    className="input2"
                    name="closing_title"
                    value={pageContent?.closing_title || ""}
                    onChange={handleInputChange}
                    onFocus={handleInputChange}
                    data-label="クロージング タイトル"
                    rows={3}
                    placeholder="タイトルを入力"
                  />
                  <textarea
                    className="input2"
                    name="closing_text"
                    value={pageContent?.closing_text || ""}
                    onChange={handleInputChange}
                    onFocus={handleInputChange}
                    data-label="クロージング テキスト"
                    rows={5}
                    placeholder="テキストを入力"
                  />
                </div>
              </section>
            </div>

            {/* 送信ボタン */}
            <div className="submit-area">
              <button type="reset" onClick={handleReset}>
                Reset
              </button>
              <button type="submit" onClick={handlePageDataSubmit}>
                Submit
              </button>
            </div>

            {/* メディア / オーバーレイ */}
            {isMediaOpen && <MediaGallery onUpload={handleDrop} />}
          </>
        )}
      </ResizableEditArea>

      {/* ローディング */}
      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}

      {/* プレビュー */}
      <Iframe
        path={"/newvisitor"}
        data={{
          ...pageContent,
          conversion_contents: conversionContents.map((c) => ({
            ...c,
            image_url: c.image?.image_url || "",
          })),
          reasons_contents: reasonsContents,
        }}
        contentKey={contentKey}
      />
    </>
  );
}

// MediaProviderでラップしたコンポーネントをエクスポート
export default function NewVisitorEditPage() {
  return (
    <MediaProvider>
      <NewVisitorEditPageContent />
    </MediaProvider>
  );
}
